import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { ChevronsUpDown, CircleX, Pencil, PencilIcon, Plus, Search, X } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "./InputDebounce";
import { deleteBucket, saveBucket } from "../api/ProjectApi";
import BucketAction from "../actions/BucketAction";
import LayoutAction from "../actions/LayoutAction";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";

interface BucketSelectProps {
  projectId: string;
  contentType: string;
  type: "INLINE" | "FORM" | "GRID" | "CUSTOM";
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isClearable?: boolean;
  popoverPanelWidth?: number;
  targetCustom?: React.ReactNode;
}

const BucketSelect = ({
  projectId,
  contentType,
  type,
  value,
  onChange,
  placeholder,
  isClearable,
  popoverPanelWidth,
  targetCustom,
}: BucketSelectProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);

  const [keywords, setKeywords] = useState("");
  const [showForm, setShowForm] = useState({
    show: false,
    id: "",
    name: "",
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType)) || fromJS([]);

  useEffect(() => {
    if (buckets.size === 0) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
    }
  }, [projectId, contentType, buckets, dispatch]);

  const handleSave = async () => {
    if (!showForm.show || !showForm.name) return;
    setIsSaving(true);
    const formData: any = {
      projectId,
      tenantId: "",
      name: showForm.name.trim(),
      order: buckets.size,
      type: contentType,
    };
    if (showForm.id) {
      formData.id = showForm.id;
    }
    const savedBucket = await saveBucket(formData);
    if (savedBucket && savedBucket.id) {
      onChange(savedBucket.id);
    }
    await dispatch(BucketAction.getBuckets(projectId, contentType));
    setShowForm({
      show: false,
      id: "",
      name: "",
    });
    setIsSaving(false);
  };

  const handleDelete = (id) => {
    setShowConfirm({
      show: true,
      title: t("Delete bucket"),
      description: t("Are you sure you want to remove this bucket?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: id,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      deleteBucket(showConfirm.data, contentType).then(() => {
        dispatch(BucketAction.getBuckets(projectId, contentType));
        dispatch(
          LayoutAction.setNotification({
            title: t("Delete bucket"),
            type: "success",
            message: t("Delete bucket successfully!"),
          })
        );
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search...")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
          delay={600}
        />
      </div>
    );
  };

  const renderBucketList = (onClose) => {
    if (buckets.size === 0) {
      return (
        <div className="flex items-center justify-center p-1 text-gray-400">{t("No options")}</div>
      );
    }
    return buckets
      .filter((item) =>
        keywords
          ? (item.get("name") || "").trim().toLowerCase().includes(keywords.trim().toLowerCase())
          : true
      )
      .map((item) => {
        return (
          <div
            key={item.get("id")}
            className="flex items-center justify-between group p-1 rounded-md hover:bg-gray-100 transition-colors gap-2 cursor-default"
          >
            <div
              className="w-full py-1.5 flex flex-col flex-nowrap overflow-hidden"
              onClick={() => {
                onChange(item.get("id"));
                onClose();
              }}
            >
              <div title={item.get("name")} className="text-sm font-medium leading-4 truncate">
                {item.get("name")}
              </div>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                onClick={() => {
                  handleDelete(item.get("id"));
                }}
                title={t("Delete bucket")}
              >
                <CircleX className="w-4 h-4 text-red-600" />
              </button>
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                onClick={() => {
                  setShowForm({
                    show: true,
                    id: item.get("id"),
                    name: item.get("name"),
                  });
                }}
                title={t("Edit bucket")}
              >
                <Pencil className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        );
      });
  };

  const renderAddOrEdit = () => {
    if (!showForm.show) {
      return (
        <div
          className="mt-2 flex items-center gap-2 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
          onClick={() => {
            setShowForm({
              show: true,
              id: "",
              name: "",
            });
          }}
        >
          <Plus className="w-4 h-4 text-gray-600" />
          <div className="text-sm font-medium text-gray-600">{t("Create new bucket")}</div>
        </div>
      );
    }
    return (
      <div className="mt-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="mt-1 text-sm font-medium text-gray-600">
            {showForm.id ? t("Edit bucket") : t("Create new bucket")}
          </div>
          <div></div>
        </div>
        <input
          autoFocus
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm outline-none"
          placeholder={t("Bucket name")}
          value={showForm.name}
          onChange={(e) => {
            setShowForm((prev) => ({ ...prev, name: e.target.value }));
          }}
        />
        <div className="flex items-center justify-between gap-2">
          <div></div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
              onClick={() => {
                setShowForm({
                  show: false,
                  id: "",
                  name: "",
                });
              }}
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              disabled={!showForm.name || isSaving}
              className="px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600"
              onClick={handleSave}
            >
              {isSaving ? t("Saving...") : showForm.id ? t("Update") : t("Add")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderTarget = (isOpen) => {
    const selected = buckets.find((item) => item.get("id") === value);
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {selected ? (
            <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden">
              <div className="text-xs font-medium leading-4">{selected.get("name")}</div>
            </div>
          ) : (
            <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
          }`}
        >
          {selected ? (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm font-medium leading-4 truncate">{selected.get("name")}</div>
            </div>
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            {isClearable && !!selected ? (
              <X
                className={`w-3.5 h-3.5 ${
                  isOpen ? "text-gray-500 hover:text-gray-700" : "text-gray-300 hover:text-gray-400"
                }`}
                strokeWidth={4}
                onClick={() => {
                  onChange(undefined);
                }}
              />
            ) : null}
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {selected ? (
            <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden cursor-default">
              <div className="text-sm font-medium leading-4">{selected.get("name")}</div>
            </div>
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    } else if (type === "CUSTOM") {
      return <>{targetCustom}</>;
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
    popoverPanelClassName = "-mt-6.5";
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  } else if (type === "CUSTOM") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <>
      <Popover>
        {({ open, close }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useLayoutEffect(() => {
            if (!open) return;
            if (!popoverButtonRef.current || !popoverPanelRef.current) return;
            const buttonWidth = popoverButtonRef.current.offsetWidth;
            const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
            const maxWidth = Math.max(buttonWidth, panelNaturalWidth);
            setPanelWidth(popoverPanelWidth || maxWidth);
          }, [open]);

          return (
            <>
              <PopoverButton
                ref={popoverButtonRef}
                as="div"
                onClick={(e) => {
                  if (open) {
                    e.preventDefault();
                  }
                }}
              >
                {renderTarget(open)}
              </PopoverButton>
              <PopoverPanel
                ref={popoverPanelRef}
                anchor="bottom start"
                style={{ width: panelWidth ?? "auto" }}
                className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
              >
                <div>
                  {renderSearch()}
                  <div className="w-full my-1 border-b border-gray-200"></div>
                  <div className="w-full max-h-60 overflow-y-auto">{renderBucketList(close)}</div>
                  <div className="w-full my-1 border-b border-gray-200"></div>
                  {renderAddOrEdit()}
                </div>
              </PopoverPanel>
            </>
          );
        }}
      </Popover>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default BucketSelect;
